import { jourIso } from './rapprochement-sorties';

/**
 * Le relevé bancaire gardé dans les réglages de la société.
 *
 * <p>Chaque import remplaçait le relevé précédent : septembre disparaissait
 * dès qu'octobre arrivait. Les mouvements s'ajoutent maintenant les uns aux
 * autres, un mouvement déjà là n'étant jamais compté deux fois, et chacun
 * garde ce que le patron en a dit (facture d'un autre logiciel, sortie déjà
 * comptée ou privée, facture payée).
 */

/** Le sens lu sur le relevé : celui choisi par le patron ne change pas la clé. */
const sensLu = (t) => t?.sensLu || t?.sens || '';

/**
 * Ce qui fait un mouvement : jour, montant, sens, libellé, référence et, si
 * la banque la donne (camt : AcctSvcrRef), sa propre référence.
 */
function base(t) {
  const parts = [
    jourIso(t?.date) || String(t?.date || ''),
    (+t?.montant || 0).toFixed(2),
    sensLu(t),
    String(t?.label || '').trim(),
    String(t?.ref || '').trim(),
  ];
  const refBanque = String(t?.refBanque || '').trim();
  if (refBanque) parts.push(refBanque);
  return parts.join('|');
}

/**
 * La clé d'un mouvement : jour, montant, sens, libellé et référence (et la
 * référence de la banque quand elle existe). Deux relevés qui se chevauchent
 * donnent la même clé pour la même ligne. Deux achats identiques du même jour
 * (deux tickets de parking à 5.–) se distinguent par leur rang (`occurrence`).
 *
 * @param {object} t mouvement ({date, montant, sens, label, ref, refBanque?, occurrence?})
 * @returns {string} la clé
 */
export function cleMouvement(t) {
  return base(t) + (+t?.occurrence > 1 ? '|#' + t.occurrence : '');
}

const JOUR_MS = 24 * 3600 * 1000;

/** Jours d'écart admis entre deux formats du même relevé (date comptable / valeur). */
const ECART_FORMATS_JOURS = 2;

/** Le même paiement lu dans un autre format (camt, puis PDF) ? */
function memePaiement(a, b) {
  if (sensLu(a) !== sensLu(b)) return false;
  if (Math.abs((+a.montant || 0) - (+b.montant || 0)) >= 0.005) return false;
  const ecart = Math.abs(Date.parse(jourIso(a.date)) - Date.parse(jourIso(b.date))) / JOUR_MS;
  if (!Number.isFinite(ecart) || ecart > ECART_FORMATS_JOURS) return false;
  const ref = (t) => String(t.ref || '').replace(/\s/g, '');
  if (ref(a) && ref(b) && ref(a) !== ref(b)) return false;
  // Deux références de la banque différentes : deux mouvements, pas un.
  if (a.refBanque && b.refBanque && a.refBanque !== b.refBanque) return false;
  return true;
}

/**
 * Ajoute les mouvements d'un nouveau relevé à ceux déjà gardés.
 *
 * <p>Un mouvement déjà là garde sa version gardée (avec ce que le patron en a
 * dit) ; le tout se range par jour, le plus ancien d'abord. Les mouvements
 * d'un même fichier ne se dédoublonnent jamais entre eux : deux tickets de
 * parking identiques le même jour sont deux sorties. Une ligne qui revient
 * n fois dans le nouveau relevé et m fois dans le relevé gardé en ajoute
 * n − m. Le même paiement lu dans un autre format (le camt, puis le PDF du
 * même mois : libellé différent) n'est pas ajouté en double à l'aveugle : il
 * est marqué `doublonPossible` (la clé de l'autre), à confirmer par le patron.
 *
 * @param {object[]} [anciens] mouvements déjà gardés
 * @param {object[]} [lus] mouvements du relevé importé
 * @param {{soldeInitial?: number, soldeFinal?: number}} [soldes] soldes du
 *   relevé importé, notés sur son premier et son dernier mouvement
 * @returns {{mouvements: object[], nouveaux: object[], douteux: object[], deja: number}}
 *   le relevé complet, les mouvements ajoutés, ceux peut-être déjà là sous un
 *   autre libellé, et le nombre de ceux qui y étaient déjà
 */
export function fusionneReleves(anciens, lus, soldes = {}) {
  const gardes = (Array.isArray(anciens) ? anciens : []).slice();
  const liste = (lus || []).map((t) => ({ ...t }));
  if (liste.length && Number.isFinite(soldes?.soldeInitial))
    liste[0].soldeAvant = soldes.soldeInitial;
  if (liste.length && Number.isFinite(soldes?.soldeFinal))
    liste[liste.length - 1].soldeApres = soldes.soldeFinal;
  // Les lignes gardées, par ce qui les fait : celles déjà prises par une ligne
  // du nouveau relevé ne servent plus.
  const parBase = new Map();
  gardes.forEach((t, i) => {
    const k = base(t);
    if (!parBase.has(k)) parBase.set(k, []);
    parBase.get(k).push(i);
  });
  const prises = new Set();
  const vues = new Map();
  const nouveaux = [];
  const douteux = [];
  const ajoutes = [];
  let deja = 0;
  for (const t of liste) {
    const k = base(t);
    const n = (vues.get(k) || 0) + 1;
    vues.set(k, n);
    const memes = parBase.get(k) || [];
    if (n <= memes.length) {
      // Déjà là : la version gardée reste, complétée des soldes du relevé.
      const i = memes[n - 1];
      prises.add(i);
      deja += 1;
      const suite = { ...gardes[i] };
      let change = false;
      for (const champ of ['soldeAvant', 'soldeApres']) {
        if (t[champ] !== undefined && suite[champ] === undefined) {
          suite[champ] = t[champ];
          change = true;
        }
      }
      if (change) gardes[i] = suite;
      continue;
    }
    if (n > 1) t.occurrence = n;
    else delete t.occurrence;
    const autre = gardes.findIndex(
      (g, i) => !prises.has(i) && !g.doublonPossible && memePaiement(g, t),
    );
    if (autre >= 0) {
      prises.add(autre);
      t.doublonPossible = cleMouvement(gardes[autre]);
      douteux.push(t);
    } else nouveaux.push(t);
    ajoutes.push(t);
  }
  const jour = (t) => jourIso(t.date) || '';
  // Tri stable : deux mouvements du même jour gardent l'ordre du relevé.
  const mouvements = [...gardes, ...ajoutes]
    .map((t, i) => ({ t, i }))
    .sort((a, b) => jour(a.t).localeCompare(jour(b.t)) || a.i - b.i)
    .map(({ t }) => t);
  return { mouvements, nouveaux, douteux, deja };
}

/**
 * Note sur un mouvement ce que le patron en a dit. Une valeur `undefined`
 * efface la note (« Annuler »).
 *
 * @param {object[]} [mouvements] relevé gardé
 * @param {string} cle clé du mouvement ({@link cleMouvement})
 * @param {object} note champs à poser, ex. `{traite: 'ailleurs'}` ou `{factureId: 12}`
 * @returns {object[]} le relevé mis à jour
 */
export function noteMouvement(mouvements, cle, note) {
  return (mouvements || []).map((t) => {
    if (cleMouvement(t) !== cle) return t;
    const suite = { ...t };
    for (const [k, v] of Object.entries(note || {})) {
      if (v === undefined) delete suite[k];
      else suite[k] = v;
    }
    return suite;
  });
}

/**
 * Le sens choisi par le patron pour un mouvement que le relevé ne disait pas
 * (« à vérifier ») : la clé reste celle du sens lu.
 *
 * @param {object} t mouvement
 * @param {'CRDT'|'DBIT'} sens le sens choisi
 * @returns {object} la note à poser ({@link noteMouvement})
 */
export const noteDuSens = (t, sens) => ({ sens, sensLu: sensLu(t) });

/**
 * Retire une ligne du relevé gardé (un doublon confirmé).
 *
 * @param {object[]} [mouvements] relevé gardé
 * @param {string} cle clé du mouvement
 * @returns {object[]} le relevé sans cette ligne
 */
export function retireMouvement(mouvements, cle) {
  return (mouvements || []).filter((t) => cleMouvement(t) !== cle);
}

/** Un mouvement dont le sens est connu (entrée ou sortie). */
export const sensConnu = (t) => t?.sens === 'CRDT' || t?.sens === 'DBIT';

const arrondi = (x) => Math.round(x * 100) / 100;

/**
 * Les totaux du relevé pour un exercice, et le contrôle des soldes : solde
 * initial + entrées − sorties = solde final, quand le relevé donne ses soldes
 * (camt : Bal OPBD / CLBD ; PDF : solde reporté et solde final). Un écart dit
 * qu'un mouvement manque ou a été mal lu. Un doublon possible ne compte pas.
 *
 * @param {object[]} [mouvements] relevé gardé
 * @param {number} [annee] l'exercice affiché ; sans lui, tout le relevé
 * @returns {{entrees: number, sorties: number, aVerifier: number,
 *   initial?: number, final?: number, ecart?: number}} les totaux et le contrôle
 */
export function totauxDuReleve(mouvements, annee) {
  const dans = (mouvements || []).filter(
    (t) => !annee || (jourIso(t.date) || '').slice(0, 4) === String(annee),
  );
  const comptes = dans.filter((t) => !t.doublonPossible);
  const somme = (liste, sens) =>
    arrondi(liste.filter((t) => t.sens === sens).reduce((s, t) => s + (+t.montant || 0), 0));
  const res = {
    entrees: somme(comptes, 'CRDT'),
    sorties: somme(comptes, 'DBIT'),
    aVerifier: dans.filter((t) => t.doublonPossible || !sensConnu(t)).length,
  };
  // Par les dates, pas par l'ordre d'enregistrement : une ligne du premier
  // jour gardée d'un import précédent sortait du contrôle et faisait croire
  // à un écart.
  const jour = (t) => jourIso(t.date) || '';
  const avecAvant = comptes.filter((t) => Number.isFinite(t.soldeAvant));
  const avecApres = comptes.filter((t) => Number.isFinite(t.soldeApres));
  if (!avecAvant.length || !avecApres.length) return res;
  const debut = avecAvant.reduce((m, t) => (jour(t) < jour(m) ? t : m));
  const fin = avecApres.reduce((m, t) => (jour(t) >= jour(m) ? t : m));
  if (jour(fin) < jour(debut)) return res;
  const periode = comptes.filter((t) => jour(t) >= jour(debut) && jour(t) <= jour(fin));
  const initial = debut.soldeAvant;
  const final = fin.soldeApres;
  const ecart = arrondi(initial + somme(periode, 'CRDT') - somme(periode, 'DBIT') - final);
  return { ...res, initial, final, ecart };
}
